
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { useDrafts } from "@/hooks/use-drafts";
import { ChevronRight } from "lucide-react";

const taskDescriptions = {
  1: "Analyze and describe visual data in detail. Transform graphs, charts, or diagrams into clear, organized text. Perfect your skills in data interpretation and academic writing in just 20 minutes.",
  2: "Present your perspective on contemporary issues through a well-structured essay. Develop arguments, provide relevant examples, and showcase critical thinking in 40 minutes of focused writing."
};

const TaskCard = ({
  taskType,
  title,
}: {
  taskType: number;
  title: string;
}) => {
  const { data: drafts, isLoading } = useDrafts({
    limit: 3,
    orderBy: 'created_at',
    orderDirection: 'desc',
    taskType
  });

  const taskDrafts = (drafts || []).filter(draft => draft.topics);

  return (
    <Card className="bg-stone-100 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 shadow-sm flex flex-col h-full">
      <div className="bg-white dark:bg-stone-800 border-b border-stone-200 dark:border-stone-700 rounded-xl">
        <CardHeader className="px-8">
          <CardTitle className="mb-3 text-3xl font-semibold dark:text-white">{title}</CardTitle>
          <p className="text-sm text-stone-500 dark:text-stone-500">
            {taskDescriptions[taskType as keyof typeof taskDescriptions]}
          </p>
        </CardHeader>
        <CardContent className="px-8 pb-6">
          <Button
            asChild
            className="w-full md:w-auto font-semibold text-white bg-orange-500 hover:bg-orange-500/90 shadow-[inset_0px_1px_0px_0px_#FFFFFF66,0px_0px_0px_1px_#C2410CCC]"
          >
            <Link to={`/practice?task=${taskType}`}>
              Start practicing
            </Link>
          </Button>
        </CardContent>
      </div>
      <CardFooter className="flex flex-col rounded-b-xl flex-1 px-8">
        <div className="w-full h-full">
          {isLoading ? (
            <div className="flex items-center justify-center h-full min-h-[120px]">
              <div className="animate-spin h-6 w-6 border-4 border-orange-500 border-t-transparent rounded-full" />
            </div>
          ) : taskDrafts.length > 0 ? (
            <>
              <div className="flex items-center justify-between py-4 border-b border-stone-200 dark:border-stone-700">
                <h4 className="text-sm text-stone-500 dark:text-stone-400 font-medium">Last drafts</h4>
                <Link
                  to={`/all-drafts?task=${taskType}`}
                  className="text-sm font-semibold text-orange-500 hover:text-orange-500/90"
                >
                  See all drafts
                </Link>
              </div>
              <div>
                {taskDrafts.map((draft) => (
                  <Link
                    key={draft.id}
                    to={`/practice?task=${taskType}&draft=${draft.id}`}
                    className="flex items-center transition-colors group py-4 border-b border-stone-200 dark:border-stone-700 last:border-b-0 text-stone-500 dark:text-stone-400 hover:text-orange-500 dark:hover:text-orange-500"
                  >
                    <p className="flex-1 text-sm font-medium truncate">{draft.topics.topic}</p>
                    <p className="flex-1 text-sm">
                      {draft.evaluations?.[0]?.overall_score ? 'Evaluated' : 'In Progress'}
                    </p>
                    <p className="flex-1 text-sm">
                      Score {draft.evaluations?.[0]?.overall_score || 'Pending'}
                    </p>
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[120px]">
              <p className="text-sm text-stone-500 dark:text-stone-400">
                No drafts yet. Give it a try!
              </p>
            </div>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export const QuickAccess = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 task-selection">
      <TaskCard taskType={1} title="Task 1" />
      <TaskCard taskType={2} title="Task 2" />
    </div>
  );
};
